import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { API } from "../App";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("session_token");
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
    return {};
  }, []);

  const checkAuth = useCallback(async () => {
    // If on the auth callback page, skip — AuthCallback handles it
    if (window.location.pathname === "/auth/callback") {
      setLoading(false);
      return false;
    }

    const token = localStorage.getItem("session_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return false;
    }

    try {
      const response = await fetch(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });

      if (!response.ok) {
        localStorage.removeItem("session_token");
        setUser(null);
        setLoading(false);
        return false;
      }

      const userData = await response.json();
      setUser(userData);
      setLoading(false);
      return true;
    } catch (error) {
      console.error("Auth check failed:", error);
      localStorage.removeItem("session_token");
      setUser(null);
      setLoading(false);
      return false;
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback((userData) => {
    setUser(userData);
    setLoading(false);
  }, []);

  const redirectToLogin = useCallback(() => {
    window.location.href = "/login";
  }, []);

  const loginWithGoogle = useCallback(() => {
    window.location.href = `${API}/auth/google/login`;
  }, []);

  const logout = useCallback(async () => {
    try {
      const token = localStorage.getItem("session_token");
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem("session_token");
    setUser(null);
    window.location.href = "/";
  }, []);

  const updateUser = useCallback((userData) => {
    setUser(userData);
    setLoading(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, redirectToLogin, loginWithGoogle, logout, checkAuth, updateUser, setLoading, getAuthHeaders }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
